import { supabase } from '@/integrations/supabase/client';
import type {
	EmailTemplate,
	EmailCampaign,
	EmailLog,
	SendEmailParams,
	SendEmailResult,
	CreateTemplateRequest,
	EmailLogFilters,
} from '@/types/email';

const getCurrentOrganization = async (): Promise<string> => {
	const { data: { user } } = await supabase.auth.getUser();
	if (!user) throw new Error('User not authenticated');

	const { data: userOrgs } = await supabase
		.from('user_organization')
		.select('organization_uuid')
		.eq('user_uuid', user.id)
		.single();

	if (!userOrgs) throw new Error('User not associated with any organization');
	return userOrgs.organization_uuid;
};

export const emailService = {
	// ─── Send Emails ─────────────────────────────────────────────────

	async sendEmail(params: SendEmailParams): Promise<SendEmailResult> {
		const { data, error } = await supabase.functions.invoke('send-job-email', {
			body: params,
		});

		if (error)
			return { success: false, error: error.message };

		return data as SendEmailResult;
	},

	// ─── Templates ───────────────────────────────────────────────────

	async getTemplates(): Promise<EmailTemplate[]> {
		const { data, error } = await supabase
			.from('email_templates')
			.select('*')
			.eq('active', true)
			.order('is_default', { ascending: false })
			.order('name', { ascending: true });

		if (error) throw error;
		return (data || []) as unknown as EmailTemplate[];
	},

	async getTemplate(id: string): Promise<EmailTemplate> {
		const { data, error } = await supabase
			.from('email_templates')
			.select('*')
			.eq('id', id)
			.single();

		if (error) throw error;
		return data as unknown as EmailTemplate;
	},

	async createTemplate(request: CreateTemplateRequest): Promise<EmailTemplate> {
		const organizationUuid = await getCurrentOrganization();
		const { data: { user } } = await supabase.auth.getUser();

		const { data, error } = await supabase
			.from('email_templates')
			.insert({
				slug: request.slug,
				name: request.name,
				description: request.description,
				category: request.category,
				subject_en: request.subject_en,
				subject_fr: request.subject_fr,
				subject_nl: request.subject_nl,
				body_en: request.body_en,
				body_fr: request.body_fr,
				body_nl: request.body_nl,
				variables: request.variables || [],
				is_default: false,
				organization_uuid: organizationUuid,
				created_by: user?.id,
			})
			.select('*')
			.single();

		if (error) throw error;
		return data as unknown as EmailTemplate;
	},

	async updateTemplate(id: string, updates: Partial<EmailTemplate>): Promise<EmailTemplate> {
		const { data, error } = await supabase
			.from('email_templates')
			.update({
				...updates,
				updated_at: new Date().toISOString(),
			})
			.eq('id', id)
			.select('*')
			.single();

		if (error) throw error;
		return data as unknown as EmailTemplate;
	},

	async deleteTemplate(id: string): Promise<void> {
		const { error } = await supabase
			.from('email_templates')
			.delete()
			.eq('id', id)
			.eq('is_default', false); // Cannot delete system defaults

		if (error) throw error;
	},

	async duplicateTemplate(id: string, newSlug: string, newName: string): Promise<EmailTemplate> {
		const original = await this.getTemplate(id);
		return this.createTemplate({
			slug: newSlug,
			name: newName,
			description: original.description,
			category: original.category,
			subject_en: original.subject_en,
			subject_fr: original.subject_fr,
			subject_nl: original.subject_nl,
			body_en: original.body_en,
			body_fr: original.body_fr,
			body_nl: original.body_nl,
			variables: original.variables,
		});
	},

	// ─── Email Logs ──────────────────────────────────────────────────

	async getEmailLogs(filters: EmailLogFilters): Promise<EmailLog[]> {
		let query = supabase
			.from('email_logs')
			.select('*')
			.eq('organization_uuid', filters.organization_uuid)
			.order('created_at', { ascending: false });

		if (filters.campaign_id)
			query = query.eq('campaign_id', filters.campaign_id);
		if (filters.candidate_id)
			query = query.eq('candidate_id', filters.candidate_id);
		if (filters.job_id)
			query = query.eq('job_id', filters.job_id);
		if (filters.status)
			query = query.eq('status', filters.status);
		if (filters.limit)
			query = query.limit(filters.limit);
		if (filters.offset)
			query = query.range(filters.offset, filters.offset + (filters.limit || 50) - 1);

		const { data, error } = await query;
		if (error) throw error;
		return (data || []) as unknown as EmailLog[];
	},

	async getEmailLogsForList(listId: string): Promise<EmailLog[]> {
		const orgUuid = await getCurrentOrganization();

		// Get campaigns for this list, then get logs
		const { data: campaigns } = await supabase
			.from('email_campaigns')
			.select('id')
			.eq('list_id', listId);

		if (!campaigns || campaigns.length === 0) return [];

		const campaignIds = campaigns.map(c => c.id);
		const { data, error } = await supabase
			.from('email_logs')
			.select('*')
			.eq('organization_uuid', orgUuid)
			.in('campaign_id', campaignIds)
			.order('created_at', { ascending: false });

		if (error) throw error;
		return (data || []) as unknown as EmailLog[];
	},

	// ─── Campaigns ───────────────────────────────────────────────────

	async getCampaigns(): Promise<EmailCampaign[]> {
		const orgUuid = await getCurrentOrganization();

		const { data, error } = await supabase
			.from('email_campaigns')
			.select('*')
			.eq('organization_uuid', orgUuid)
			.order('created_at', { ascending: false });

		if (error) throw error;
		return (data || []) as unknown as EmailCampaign[];
	},

	async getCampaignsForList(listId: string): Promise<EmailCampaign[]> {
		const orgUuid = await getCurrentOrganization();

		const { data, error } = await supabase
			.from('email_campaigns')
			.select('*')
			.eq('organization_uuid', orgUuid)
			.eq('list_id', listId)
			.order('created_at', { ascending: false });

		if (error) throw error;
		return (data || []) as unknown as EmailCampaign[];
	},

	async getCampaignsForJob(jobId: string): Promise<EmailCampaign[]> {
		const orgUuid = await getCurrentOrganization();

		const { data, error } = await supabase
			.from('email_campaigns')
			.select('*')
			.eq('organization_uuid', orgUuid)
			.eq('job_id', jobId)
			.order('created_at', { ascending: false });

		if (error) throw error;
		return (data || []) as unknown as EmailCampaign[];
	},
};
